import React, { createContext, useState, useEffect, useContext, useCallback } from "react";
import { updateWorkflow, clearStunMStorage } from "./utils/workflowUtils";
import { loginConsumer } from "./api/authService";
import { defaultWorkspace } from "./utils/workspaceRoles";
import { defaultAssignedRole } from "./utils/roleNavigation";
import { isAuthenticationError } from "./api/requestAuth";

const AuthContext = createContext();

// Helper functions for localStorage
const loadFromLocalStorage = (key, defaultValue) => {
  try {
    const storedValue = localStorage.getItem(key);
    return storedValue !== null ? JSON.parse(storedValue) : defaultValue;
  } catch (error) {
    console.error(
      `Error parsing JSON from localStorage for key "${key}":`,
      error
    );
    return defaultValue;
  }
};

const saveToLocalStorage = (key, value) => {
  localStorage.setItem(key, JSON.stringify(value));
};

// Session restoration is only a convenience for an existing HttpOnly-cookie
// session. Keep the wait short so an unavailable API never leaves navigation
// blocked behind a loading screen. Protected routes still fail closed once
// restoration finishes.
export const SESSION_RESTORE_TIMEOUT_MS = 5000;

export const AuthProvider = ({ children }) => {
  // Load initial state from localStorage or set defaults
  // Authentication is confirmed by the backend session endpoint. Do not use
  // the legacy localStorage flag to render protected pages optimistically.
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [sessionRestoreError, setSessionRestoreError] = useState("");
  const [sessionRestoreAttempt, setSessionRestoreAttempt] = useState(0);
  const retrySessionRestore = useCallback(() => {
    setAuthReady(false);
    setSessionRestoreError("");
    setSessionRestoreAttempt((attempt) => attempt + 1);
  }, []);
  const [isApproverLoggedIn, setIsApproverLoggedIn] = useState(false);
  // This is an in-memory UI preference, deliberately not authorization and
  // deliberately not persisted. A new/restored owner session starts in admin.
  const [activeWorkspace, setActiveWorkspace] = useState("admin");
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [activeRole, setActiveRole] = useState("");
  const [brainStormFlag, setBrainStormFlag] = useState(
    loadFromLocalStorage("brainStormFlag", false)
  );
  const [buttonBrainStormBackground, setButtonBrainStormBackground] = useState(
    loadFromLocalStorage("buttonBrainStormBackground", false)
  );
  const [displayBrainStorm, setDisplayBrainStorm] = useState(
    loadFromLocalStorage("displayBrainStorm", false)
  );
  const [displayCampaignBreif, setDisplayCampaignBreif] = useState(
    loadFromLocalStorage("displayCampaignBreif", false)
  );
  const [profile, setProfile] = useState(
    loadFromLocalStorage("profile", { orgname: "" })
  );
  const [email, setEmail] = useState(loadFromLocalStorage("email", ""));
  const [subscription_id, setSubscription_id] = useState(
    loadFromLocalStorage("subscription_id", "")
  );
  // Tokens are no longer persisted in web storage. The backend restores the
  // authenticated session from its HttpOnly cookie after a page reload.
  const [token, setToken] = useState("");
  const [user_type, setUser_type] = useState(
    loadFromLocalStorage("user_type", "")
  );
  const [user_type_new, setUser_type_new] = useState(
    loadFromLocalStorage("user_type_new", "")
  );

  // Apply the same consumer session shape for full login and feature-level
  // Quick Login. Feature flows can then retry their pending action without
  // losing the organization, role, or user identity established by login.
  const authenticateConsumer = async ({ email: loginEmail, password, organizationId }) => {
    const data = await loginConsumer({
      apiUrl,
      email: loginEmail,
      password,
      organization_id: organizationId,
    });
    if (!data?.access_token) throw new Error("Login did not return a valid session.");

    const consumerId = data.approver_id || data.id || "";
    setEmail(loginEmail);
    setSubscription_id(Number(organizationId));
    setSubscriptionCheck({ subscriptionId: Number(organizationId), status: "unknown" });
    setToken(data.access_token);
    setProfile(data);
    setUser_type(data.user_type || "user");
    setUser_type_new(data.user_type || "user");
    setApproverId(consumerId);
    setLoggedInUserId(consumerId);
    setIsApproverLoggedIn(Boolean(data.is_approver));
    setActiveWorkspace(
      defaultWorkspace({
        userType: data.user_type || "user",
        isApproverLoggedIn: Boolean(data.is_approver),
      })
    );
    setIsLoggedIn(true);
    setSessionRestoreError("");
    return data;
  };
  const [approverId, setApproverId] = useState(
    loadFromLocalStorage("approverId", "")
  );
  const [loggedInUserId, setLoggedInUserId] = useState(
    loadFromLocalStorage("loggedInUserId", "")
  );
  const [apiUrl, setApiUrl] = useState(process.env.REACT_APP_API_URL || "");
  const refreshSessionRoles = useCallback(async () => {
    const response = await fetch(`${apiUrl}/auth/session`, { credentials: "include" });
    if (!response.ok) throw new Error("Unable to load assigned roles");
    const session = await response.json();
    const roles = Array.isArray(session.accessconsumer) ? session.accessconsumer : [];
    setAssignedRoles(roles);
    if (session.consumer_id) setLoggedInUserId(session.consumer_id);
    setActiveRole((selected) => selected && roles.includes(selected) ? selected : defaultAssignedRole(roles, session.user_type));
    setIsApproverLoggedIn(Boolean(session.is_approver));
    setActiveWorkspace((workspace) => {
      if (workspace === "approver" && !session.is_approver) return "admin";
      return workspace;
    });
    return session;
  }, [apiUrl]);
  const [hubname, setHubname] = useState(process.env.REACT_APP_HUB_NAME || "");
  const [canvaClientId, setCanvaClientId] = useState(process.env.REACT_APP_CANVA_CLIENT_ID || "");
  const [canvaCallbaclUrl, setCanvaCallbaclUrl] = useState(process.env.REACT_APP_CANVA_CALLBACK_URL || "");
  const [wsUrl, setWsUrl] = useState();
  const [alerts, setAlerts] = useState([]); // Use a Set to store alerts
  const addAlert = (newAlert) => {
    setAlerts((prevAlerts) => {
      const prevAlertIds = new Set(prevAlerts.map((alert) => alert.id)); // Extract IDs from previous alerts

      if (newAlert && newAlert.id) {
        if (!prevAlertIds.has(newAlert.id)) {
          setUnreadCount((prevCount) => prevCount + 1);
          return [...prevAlerts, newAlert]; // Use an array instead of a Set
        }
      } else {
        console.error("Alert missing ID:", newAlert);
      }
      return prevAlerts;
    });
  };
  const [selectedAssetType, setSelectedAssetType] = useState();
  const [unreadCount, setUnreadCount] = useState(0);
  // asset related
  const [assignedResourceList, setAssignedResourceList] = useState([]);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);
  const [selectedOfferings, setSelectedOfferings] = useState([]);
  // approver related state
  const [summaryDataForApprover, setSummaryDataForApprover] = useState({});
  const [summaryDataForCmo, setSummaryDataForCmo] = useState({});
  const [summaryDataForCmoOfferings, setSummaryDataForCmoOfferings] = useState({});

  // asset related state
  const [assetList, setAssetList] = useState([]);
  // Campaign-related state
  const [strategyFileName1, setStrategyFileName1] = useState("");
  const [socialMediaData, setSocialMediaData] = useState([[], [], [], []]);
  const [socialMediaDataForPlanning, setSocialMediaDataForPlanning] = useState(
    []
  );
  const [campaignName, setCampaignName] = useState("");
  const [campaignId, setCampaignId] = useState(0);
  const [campaignIdOnCreateCampaign, setCampaignIdOnCreateCampaign] =
    useState(0);
  const [strategyFileNameForViewFileType, setStrategyFileNameForViewFileType] =
    useState("");
  const [strategyExportFormat, setStrategyExportFormat] = useState("");
  const [strategyFileName, setStrategyFileName] = useState("");
  const [strategyFileNameForView, setStrategyFileNameForView] = useState("");
  const [description, setDescription] = useState("");
  const [timeline, setTimeline] = useState("");
  const [deliverables, seDeliverables] = useState("");
  const [selectedRadio, setSelectedRadio] = useState("yes");
  const [campaignSelectedOffering, setCampaignSelectedOffering] =
    useState(null);
  const [campaignSelectedPlatform, setCampaignSelectedPlatform] =
    useState(null);
  const [campaignSelectedBU, setCampaignSelectedBU] = useState(null);
  const [offeringSelected, setOfferingSelected] = useState(false);
  const [platformSelected, setPlatformSelected] = useState(false);
  const [campaignSelectedGidIndex, setCampaignSelectedGidIndex] = useState(-1);
  const [campaignSelectedDropdownIndex, setCampaignSelectedDropdownIndex] =
    useState(-1);
  const [displaySendCampaignBriefsBU, setDisplaySendCampaignBriefsBU] =
    useState(false);
  const [menuToggle, setMenuToggle] = useState(false);
  const [displayActionItemApproval, setDisplayActionItemApproval] =
    useState(false);
  const [actionItemApprovalFlag, setActionItemApprovalFlag] = useState(false);
  const [
    buttonActionItemApprovalBackground,
    setButtonActionItemApprovalBackground,
  ] = useState(false);
  const [
    buttonSendCampaignBriefsBUBackground,
    setButtonSendCampaignBriefsBUBackground,
  ] = useState(false);
  const [sendCampaignBriefsBUFlag, setSendCampaignBriefsBUFlag] =
    useState(false);
  const [campaignBreifFlag, setCampaignBreifFlag] = useState(false);
  const [buttonCampaignBreifBackground, setButtonCampaignBreifBackground] =
    useState(false);
  const [budgetList, setBudgetList] = useState([]);
  const [campaignList, setCampaignList] = useState([]);
  const [currentPlatformList, setCurrentPlatformList] = useState([]);
  const [campaignInitialized, setCampaignInitialized] = useState(false);
  const [subscriptionid, setSubscriptionid] = useState("");
  const [message, setMessage] = useState("");
  const [htmlText, setHtmlText] = useState("");
  const [buList, setBuList] = useState([]);
  const [createdAssetType, setCreatedAssetType] = useState(null);
  const [selected, setSelected] = useState({
    LOCATIONS: [],
    JOB_TITLES: [],
    JOB_FUNCTIONS: [],
    JOB_SENIORITIES: [],
    COMPANY_INDUSTRIES: [],
    COMPANY_SIZES: [],
    COMPANY_TYPES: [],
  });
  const [
    fileBlobGeneratedForAdminByAiModel,
    setFileBlobGeneratedForAdminByAiModel,
  ] = useState(null);
  const [
    fileNameGeneratedForAdminByAiModel,
    setFileNameGeneratedForAdminByAiModel,
  ] = useState(null);
  const [
    fileTypeGeneratedForAdminByAiModel,
    setFileTypeGeneratedForAdminByAiModel,
  ] = useState(null);
  const [
    fileUrlGeneratedForAdminByAiModel,
    setFileUrlGeneratedForAdminByAiModel,
  ] = useState(null);
  const [generatedAssetIdForAdminByAiModel, setGeneratedAssetIdForAdminByAiModel] =
    useState(null);
  const [linkedInDetails, setLinkedInDetails] = useState(null);
  const [googleAdsDetails, setGoogleAdsDetails] = useState(null);
  const [metaDetails, setMetaDetails] = useState(null);
  const [mailchimpDetails, setMailchimpDetails] = useState(null);
  const [approvedCampaignList, setApprovedCampaignList] = useState([]);
  const [approvedActiveCampaignList, setApprovedActiveCampaignList] = useState(
    []
  );
  const [assignedResourceDropdown, setAssignedResourceDropdown] = useState([]);
  const [timezoneDetails, setTimezoneDetails] = useState(null);
  const [outputFromAI, setOutputFromAI] = useState();
  const [inputPrompt, setInputPrompt] = useState();
  const [subject, setSubject] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [username, setUsername] = useState(
    process.env.REACT_APP_API_USERNAME || ""
  );
  const [password, setPassword] = useState(
    process.env.REACT_APP_API_PASSWORD || ""
  );
  const [isFree7DaysTrial, setIsFree7DaysTrial] = useState(false);
  const [daysRemainingFreeTrial, setDaysRemainingFreeTrial] = useState(7);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionCheck, setSubscriptionCheck] = useState({
    subscriptionId: null,
    status: "unknown",
  });
  const [subscriptionTier, setSubscriptionTier] = useState("free");
  const [workflow, setWorkflow] = useState([
    {
      id: 0,
      title: "Business Unit",
      subTitleEg: "BU e.g.Sample Business Unit - Enablement Team",
      subtitleDownloadFile: "Download BU List from this excel template",
      subtitleUploadloadFile: "Upload BU List",
      fileUploadedTitle: "BU List uploaded",
      subtitleManuallyCreate: "Create the BUs here manually",
      fileUploaded: false,
      manualSetupDone: false,
      stepCompleted: false,
      currentEditing: false,
      description:
        "Upload BU List from this excel template Or Create the BUs here manually Offerings e.g. Sample Offering - Build with LLM Upload Offerings List from this excel template Or Create offerings here Marketing Assets Sample Asset One Pager Upload Marketing Assets Availability List from this excel template Or Create the list here ",
      // learnMore: false,
      learnMoreTextTodo: "Let’s set up your BUs first",
      learnMoreTextDone: "Great Job! Let's move to next step Offerings",
      learnMoreText: "Let’s move to next step Offerings",
      createManually: "Or Create the BUs here manually",
      url: "/offerings",
      fileUploadUrl: apiUrl + "/api/upload-businessunits-template",
      fileUrl: apiUrl + "/api/download-businessunits-templates/",
    },
    {
      id: 1,
      title: "Offerings",
      subTitleEg: "e.g. Sample Offering - Build with LLM",
      subtitleDownloadFile: "Download Offerings List from this excel template",
      subtitleUploadloadFile: "Upload Offerings List",
      subtitleManuallyCreate: "Create offerings here",
      fileUploadedTitle: "Offerings List uploaded",
      fileUploaded: false,
      manualSetupDone: false,
      stepCompleted: false,
      currentEditing: false,
      description:
        "Upload Offerings List from this excel template Or Create offerings here",
      // learnMore: false,
      learnMoreTextTodo: "Let’s set up your Offerings first",
      learnMoreTextDone: "Great Job! Let’s move to next step marketing",
      learnMoreText: "Let’s move to next step Create content",
      createManually: "Or Create offerings here",
      url: "/marketing",
      fileUploadUrl: apiUrl + "/api/upload-offerings-template",
      fileUrl: apiUrl + "/api/download-offerings-templates/",
    },
    {
      id: 2,
      title: "Marketing Assets",
      subTitleEg: "Sample Asset One Pager",
      subtitleDownloadFile:
        "Download Marketing Assets Availability List from this excel template",
      subtitleUploadloadFile: "Upload Marketing Assets",
      fileUploadedTitle: "Marketing Assets List uploaded",
      subtitleManuallyCreate: "Create fresh assets here",
      fileUploaded: false,
      manualSetupDone: false,
      stepCompleted: false,
      currentEditing: false,
      description:
        "Upload Marketing Assets Availability List from this excel template Or Create the list here manually",
      // learnMore: false,
      learnMoreTextTodo: "Let’s set up your Marketing Assets first",
      learnMoreTextDone: "Great Job! Let’s move to next step Create content",
      learnMoreText: "Let’s move to next step Create content",
      createManually: "Or Create the list here manually",
      url: "/content",
      fileUploadUrl: apiUrl + "/api/upload-marketing-template",
      fileUrl: apiUrl + "/api/download-marketing-templates/",
    },
    {
      id: 3,
      title: "Content",
      subTitleEg:
        "e. g. Social Media​, EDMs​, Events, Website, SMS & WATI​, BDM, Partner Center​, Agencies",
      subtitleDownloadFile: "Download content from this Excel template",
      subtitleUploadloadFile: "Upload content",
      fileUploadedTitle: "Content uploaded",
      subtitleManuallyCreate: "",
      fileUploaded: false,
      manualSetupDone: false,
      stepCompleted: false,
      currentEditing: false,
      description: "Let’s upload your content or Create content here manually",
      // learnMore: false,
      learnMoreTextTodo: "Let’s curate your content first",
      learnMoreTextDone: "Great Job! Let’s move to next step Campaigns",
      learnMoreText: "Great Job! Let’s move to next step Campaigns",
      createManually: "Curate content here",
      url: "/campaign",
      fileUrl: apiUrl + "/api/download-marketing-templates/",
    },
    {
      id: 4,
      title: "Campaign",
      subTitleEg: "Campaign",
      subtitleDownloadFile: "Download Campaign from this excel template",
      subtitleUploadloadFile: "Upload Campaign",
      fileUploadedTitle: "Campaign uploaded",
      subtitleManuallyCreate: "",
      fileUploaded: false,
      manualSetupDone: false,
      stepCompleted: false,
      currentEditing: false,
      description: "Let’s set up your campaigns now",
      learnMoreTextTodo: "Let’s set up your campaigns now",
      learnMoreTextDone: "Great Job! Campaigns complete",
      learnMoreText: "Great Job! Let’s move to next step Campaigns",
      url: "/campaign",
      createManually: "Let’s set up your campaigns now​",
      fileUploadUrl: apiUrl + "/api/upload-offerings-template",
      fileUrl: apiUrl + "/api/download-marketing-templates/",
    },
    // Add more card data as needed
  ]);

  const [workflowCampaign, setWorkflowCampaign] = useState([
    {
      id: 0,
      title: "Plan & Create",
      subTitleEg: "WIP Campaigns​",
      dropdownList: [
        "Planning & Strategy​",
        "Create Campaigns",
        "Draft Campaigns",
        "Approved Campaigns",
      ],
      // subtitleDownloadFile:"Download BU List from this excel template",
      // subtitleUploadloadFile:"Upload BU List",
      // fileUploadedTitle:"BU List uploaded",
      // subtitleManuallyCreate:"Create the BUs here manually",
      // fileUploaded: false,
      // manualSetupDone:false,
      // stepCompleted:false,
      // currentEditing: false,
      // description:
      //   "Upload BU List from this excel template Or Create the BUs here manually Offerings e.g. Sample Offering - Build with LLM Upload Offerings List from this excel template Or Create offerings here Marketing Assets Sample Asset One Pager Upload Marketing Assets Availability List from this excel template Or Create the list here ",
      // // learnMore: false,
      // learnMoreTextTodo: "Let’s set up your BUs first",
      // learnMoreTextDone: "Great Job! Let's move to next step Offerings",
      // learnMoreText: "Let’s move to next step Offerings",
      // createManually:"Or Create the BUs here manually",
      // url: "/offerings",
      // fileUploadUrl: apiUrl + "/api/upload-businessunits-template",
      // fileUrl: apiUrl + "/api/download-businessunits-templates"
    },
    {
      id: 1,
      title: "Review & Approve ",
      subTitleEg: "Review/Approved - Campaigns​",
      dropdownList: ["Review​​", "Gone for Approval", "Approve"],
      // subtitleDownloadFile:"Download Offerings List from this excel template",
      // subtitleUploadloadFile:"Upload Offerings List",
      // subtitleManuallyCreate:"Create offerings here",
      // fileUploadedTitle:"Offerings List uploaded",
      // fileUploaded: false,
      // manualSetupDone:false,
      // stepCompleted:false,
      // currentEditing: false,
      // description:
      // "Upload Offerings List from this excel template Or Create offerings here",
      // // learnMore: false,
      // learnMoreTextTodo: "Let’s set up your Offerings first",
      // learnMoreTextDone: "Great Job! Let’s move to next step marketing",
      // learnMoreText: "Let’s move to next step Create content",
      // createManually:"Or Create offerings here",
      // url: "/marketing",
      // fileUploadUrl: apiUrl + "/api/upload-offerings-template",
      // fileUrl: apiUrl + "/api/download-offerings-templates"
    },
    {
      id: 2,
      title: "Launch & Publish",
      subTitleEg: "To Launch/Published/Active Campaigns​",
      dropdownList: ["Need To Launch​", "Published", "Active"],

      // subtitleDownloadFile:"Download Marketing Assets Availability List from this excel template",
      // subtitleUploadloadFile:"Upload Marketing Assets",
      // fileUploadedTitle:"Marketing Assets List uploaded",
      // subtitleManuallyCreate:"Create fresh assets here",
      // fileUploaded: false,
      // manualSetupDone:false,
      // stepCompleted:false,
      // currentEditing: false,
      // description:
      // "Upload Marketing Assets Availability List from this excel template Or Create the list here manually",
      // // learnMore: false,
      // learnMoreTextTodo: "Let’s set up your Marketing Assets first",
      // learnMoreTextDone: "Great Job! Let’s move to next step Create content",
      // learnMoreText: "Let’s move to next step Create content",
      // createManually: "Or Create the list here manually",
      // url: "/content",
      // fileUploadUrl: apiUrl + "/api/upload-marketing-template",
      // fileUrl: apiUrl + "/api/download-marketing-templates/"
    },
    // Add more card data as needed
  ]);
  // Save state changes to localStorage
  useEffect(() => {
    saveToLocalStorage("isLoggedIn", isLoggedIn);
    saveToLocalStorage("isApproverLoggedIn", isApproverLoggedIn);
    saveToLocalStorage("brainStormFlag", brainStormFlag);
    saveToLocalStorage(
      "buttonBrainStormBackground",
      buttonBrainStormBackground
    );
    saveToLocalStorage("displayBrainStorm", displayBrainStorm);
    saveToLocalStorage("displayCampaignBreif", displayCampaignBreif);
    saveToLocalStorage("profile", profile);
    saveToLocalStorage("email", email);
    saveToLocalStorage("subscription_id", subscription_id);
    saveToLocalStorage("user_type", user_type);
    saveToLocalStorage("user_type_new", user_type_new);
    saveToLocalStorage("approverId", approverId);
    saveToLocalStorage("loggedInUserId", loggedInUserId);
    saveToLocalStorage("campaignId", campaignId);
  }, [
    isLoggedIn,
    brainStormFlag,
    buttonBrainStormBackground,
    displayBrainStorm,
    displayCampaignBreif,
    profile,
    email,
    subscription_id,
    user_type,
    approverId,
    loggedInUserId,
    campaignId,
    isApproverLoggedIn,
    user_type_new,
  ]);

  // Service modules use this small bridge when they are called outside a
  // component. Keep it synchronized with the authoritative React auth state
  // so post-login requests never fall back to an empty/stale storage token.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    window.__authContext = { apiUrl, token };
    return () => {
      if (window.__authContext?.token === token) delete window.__authContext;
    };
  }, [apiUrl, token]);

  useEffect(() => {
    const handleSessionRefresh = (event) => {
      const freshToken = event?.detail?.access_token;
      if (freshToken) setToken(freshToken);
    };
    window.addEventListener("stunm:session-refreshed", handleSessionRefresh);
    return () => window.removeEventListener("stunm:session-refreshed", handleSessionRefresh);
  }, []);

  useEffect(() => {
    if (!authReady || !isLoggedIn) return;
    refreshSessionRoles().catch((error) => {
      console.warn("Unable to refresh assigned roles", error);
    });
  }, [authReady, isLoggedIn, refreshSessionRoles]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SESSION_RESTORE_TIMEOUT_MS);

    fetch(`${apiUrl}/auth/session`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (response.ok) return response.json();
        const payload = await response.json().catch(() => null);
        if (isAuthenticationError({
          response: { status: response.status, data: payload },
          sessionRefreshFailed: response.sessionRefreshFailed,
        })) return null;
        throw new Error("Unable to verify the existing session");
      })
      .then((session) => {
        if (cancelled || !session) return;
        setSessionRestoreError("");
        if (session.subscription_id) setSubscription_id(session.subscription_id);
        if (session.email) setEmail(session.email);
        if (session.user_type) setUser_type(session.user_type);
        if (session.approver_id) {
          setApproverId(session.approver_id);
          setLoggedInUserId(session.approver_id);
        }
        if (session.user_type === "admin") setUser_type_new("admin");
        setAssignedRoles(Array.isArray(session.accessconsumer) ? session.accessconsumer : []);
        setActiveRole(defaultAssignedRole(Array.isArray(session.accessconsumer) ? session.accessconsumer : [], session.user_type));
        if (session.consumer_id) setLoggedInUserId(session.consumer_id);
        setIsApproverLoggedIn(Boolean(session.is_approver));
        setActiveWorkspace(
          defaultWorkspace({
            userType: session.user_type || "user",
            isApproverLoggedIn: Boolean(session.is_approver),
          })
        );
        setIsLoggedIn(true);
      })
      .catch((error) => {
        if (cancelled) return;
        setSessionRestoreError("We could not verify your session. Please try again.");
        if (error?.name !== "AbortError") {
          console.warn("Unable to restore the existing session; verification can be retried.", error);
        }
      })
      .finally(() => {
        clearTimeout(timeoutId);
        if (!cancelled) setAuthReady(true);
      });
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [apiUrl, sessionRestoreAttempt]);

  useEffect(() => {
    if (!authReady || sessionRestoreError) return;
    if (!isLoggedIn) {
      setToken("");
      setSubscription_id("");
    }
  }, [authReady, isLoggedIn, sessionRestoreError]);

  // Ensure workflow fileUrl/fileUploadUrl carry the subscription id even when
  // the session is restored from localStorage (page reload) instead of a
  // fresh login. Idempotent — safe if login already applied it.
  useEffect(() => {
    if (subscription_id) {
      setWorkflow((prevWorkflow) => updateWorkflow(prevWorkflow, subscription_id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscription_id]);

  const setLearnMoreById = (id, value) => {
    // Workflow components pass a complete updated array after upload or
    // manual setup. Preserve that existing contract as well as id/value use.
    if (Array.isArray(id) && value === undefined) {
      setWorkflow(id);
      return;
    }
    const updatedWorkflow = workflow.map((item) =>
      item.id === id ? { ...item, learnMore: value } : item
    );
    setWorkflow(updatedWorkflow);
  };
  const setFileuploadedById = (id, value) => {
    const updatedWorkflow = workflow.map((item) =>
      item.id === id ? { ...item, fileUploaded: value } : item
    );
    setWorkflow(updatedWorkflow);
  };

  const setStepCompletedById = (id, value) => {
    const updatedWorkflow = workflow.map((item) =>
      item.id === id ? { ...item, stepCompleted: value } : item
    );
    setWorkflow(updatedWorkflow);
  };

  const getCurrentEditingById = (id) => {
    const item = workflow.find((item) => item.id === id);
    return item ? item.currentEditing : null;
  };

  // const getCurrentEditingById1 = (index,data) => {
  //   console.log(" in getCurrentEditingById1 data ",data)
  //   console.log(" in getCurrentEditingById1 index ",index)
  //   const item = workflow.find(item => item.id === data.id);
  //   return item ? item.currentEditing : null;
  // };

  // 🚀 Logout function
  const logout = () => {
    window.dispatchEvent(new Event("stunm:session-changing"));
    // Clear local state immediately so a slow API cannot leave the user
    // appearing authenticated. The bounded request still clears the HttpOnly
    // cookie when the backend is reachable.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000);
    const serverLogout = fetch(`${apiUrl}/auth/logout`, {
      method: "POST",
      credentials: "include",
      signal: controller.signal,
    })
      .catch(() => undefined)
      .finally(() => clearTimeout(timeoutId));
    clearStunMStorage();

    // Reset all context state
    setIsLoggedIn(false);
    setSessionRestoreError("");
    setIsApproverLoggedIn(false);
    setActiveWorkspace("admin");
    setAssignedRoles([]);
    setActiveRole("");
    setBrainStormFlag(false);
    setButtonBrainStormBackground(false);
    setDisplayBrainStorm(false);
    setDisplayCampaignBreif(false);
    setProfile({ orgname: "" });
    setEmail("");
    setSubscription_id("");
    setSubscriptionCheck({ subscriptionId: null, status: "unknown" });
    setToken("");
    setUser_type("");
    setUser_type_new("");
    setApproverId("");
    setLoggedInUserId("");
    setAlerts([]);
    setUnreadCount(0);
    setSelectedAssetType(undefined);
    setAssignedResourceList([]);
    setSelectedBusinessUnits([]);
    setSelectedOfferings([]);
    setSummaryDataForApprover({});
    setSummaryDataForCmo({});
    setSummaryDataForCmoOfferings({});
    setAssetList([]);
    setSocialMediaData([[], [], [], []]);
    setSocialMediaDataForPlanning([]);
    setCampaignName("");
    setCampaignId(0);
    setCampaignIdOnCreateCampaign(0);
    setStrategyFileName("");
    setStrategyFileName1("");
    setStrategyFileNameForView("");
    setStrategyFileNameForViewFileType("");
    setStrategyExportFormat("");
    setDescription("");
    setTimeline("");
    seDeliverables("");
    setSelectedRadio("yes");
    setCampaignSelectedOffering(null);
    setCampaignSelectedPlatform(null);
    setCampaignSelectedBU(null);
    setOfferingSelected(false);
    setPlatformSelected(false);
    setCampaignSelectedGidIndex(-1);
    setCampaignSelectedDropdownIndex(-1);
    setDisplaySendCampaignBriefsBU(false);
    setMenuToggle(false);
    setDisplayActionItemApproval(false);
    setActionItemApprovalFlag(false);
    setButtonActionItemApprovalBackground(false);
    setButtonSendCampaignBriefsBUBackground(false);
    setSendCampaignBriefsBUFlag(false);
    setCampaignBreifFlag(false);
    setButtonCampaignBreifBackground(false);
    setBudgetList([]);
    setCampaignList([]);
    setCurrentPlatformList([]);
    setCampaignInitialized(false);
    setSubscriptionid("");
    setMessage("");
    setHtmlText("");
    setBuList([]);
    setCreatedAssetType(null);
    setSelected({
      LOCATIONS: [],
      JOB_TITLES: [],
      JOB_FUNCTIONS: [],
      JOB_SENIORITIES: [],
      COMPANY_INDUSTRIES: [],
      COMPANY_SIZES: [],
      COMPANY_TYPES: [],
    });
    setFileBlobGeneratedForAdminByAiModel(null);
    setFileNameGeneratedForAdminByAiModel(null);
    setFileTypeGeneratedForAdminByAiModel(null);
    setFileUrlGeneratedForAdminByAiModel(null);
    setGeneratedAssetIdForAdminByAiModel(null);
    setLinkedInDetails(null);
    setGoogleAdsDetails(null);
    setMetaDetails(null);
    setMailchimpDetails(null);
    setTimezoneDetails(null);
    setOutputFromAI(undefined);
    setInputPrompt(undefined);
    setSubject("");
    setEmailTo("");
    setEmailContent("");
    setIsFree7DaysTrial(false);
    setDaysRemainingFreeTrial(7);

    console.log("✅ User logged out, session cleared.");
    return serverLogout;
  };
  const setCurrentEditingById = (id, value) => {
    console.log(" in setCurrentEditingById id  ", id, " = ", value);
    const updatedWorkflow = workflow.map((item) =>
      item.id === id ? { ...item, currentEditing: value } : item
    );
    setWorkflow(updatedWorkflow);
  };

  const getStepCompletedById = (id) => {
    const item = workflow.find((item) => item.id === id);
    return item ? item.stepCompleted : null;
  };
  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        authReady,
        sessionRestoreError,
        retrySessionRestore,
        setIsLoggedIn,
        profile,
        setProfile,
        email,
        setEmail,
        subscription_id,
        setSubscription_id,
        setSubscriptionid,
        subscriptionid,
        token,
        setToken,
        apiUrl,
        setApiUrl,
        username,
        setUsername,
        password,
        setPassword,
        workflow,
        setLearnMoreById,
        setFileuploadedById,
        setStepCompletedById,
        getStepCompletedById,
        getCurrentEditingById,
        setCurrentEditingById,
        isFree7DaysTrial,
        setIsFree7DaysTrial,
        daysRemainingFreeTrial,
        setDaysRemainingFreeTrial,
        isSubscribed,
        setIsSubscribed,
        subscriptionCheck,
        setSubscriptionCheck,
        subscriptionTier,
        setSubscriptionTier,
        setWorkflowCampaign,
        workflowCampaign,
        user_type,
        setUser_type,
        user_type_new,
        setUser_type_new,
        menuToggle,
        setMenuToggle,
        setHtmlText,
        htmlText,
        brainStormFlag,
        setBrainStormFlag,
        buttonBrainStormBackground,
        setButtonBrainStormBackground,
        actionItemApprovalFlag,
        setActionItemApprovalFlag,
        buttonActionItemApprovalBackground,
        setButtonActionItemApprovalBackground,
        displayBrainStorm,
        setDisplayBrainStorm,
        displayActionItemApproval,
        setDisplayActionItemApproval,
        campaignBreifFlag,
        buttonCampaignBreifBackground,
        setCampaignBreifFlag,
        displayCampaignBreif,
        setDisplayCampaignBreif,
        setButtonCampaignBreifBackground,
        setSendCampaignBriefsBUFlag,
        setButtonSendCampaignBriefsBUBackground,
        sendCampaignBriefsBUFlag,
        buttonSendCampaignBriefsBUBackground,
        displaySendCampaignBriefsBU,
        setDisplaySendCampaignBriefsBU,
        campaignSelectedGidIndex,
        setCampaignSelectedGidIndex,
        campaignSelectedDropdownIndex,
        setCampaignSelectedDropdownIndex,
        isApproverLoggedIn,
        setIsApproverLoggedIn,
        activeWorkspace,
        setActiveWorkspace,
        assignedRoles,
        activeRole,
        setActiveRole,
        refreshSessionRoles,
        approverId,
        setApproverId,
        fileBlobGeneratedForAdminByAiModel,
        setFileBlobGeneratedForAdminByAiModel,
        fileNameGeneratedForAdminByAiModel,
        setFileNameGeneratedForAdminByAiModel,
        campaignSelectedOffering,
        setCampaignSelectedOffering,
        campaignSelectedPlatform,
        setCampaignSelectedPlatform,
        selectedAssetType,
        setSelectedAssetType,
        loggedInUserId,
        setLoggedInUserId,
        budgetList,
        setBudgetList,
        campaignInitialized,
        setCampaignInitialized,
        message,
        setMessage,
        createdAssetType,
        setCreatedAssetType,
        offeringSelected,
        setOfferingSelected,
        platformSelected,
        setPlatformSelected,
        outputFromAI,
        setOutputFromAI,
        buList,
        setBuList,
        campaignSelectedBU,
        setCampaignSelectedBU,
        inputPrompt,
        setInputPrompt,
        subject,
        setSubject,
        emailTo,
        setEmailTo,
        emailContent,
        setEmailContent,
        selectedRadio,
        setSelectedRadio,
        campaignName,
        setCampaignName,
        description,
        setDescription,
        timeline,
        setTimeline,
        deliverables,
        seDeliverables,
        socialMediaData,
        setSocialMediaData,
        strategyFileName,
        setStrategyFileName,
        strategyExportFormat,
        setStrategyExportFormat,
        strategyFileNameForView,
        setStrategyFileNameForView,
        strategyFileNameForViewFileType,
        setStrategyFileNameForViewFileType,
        strategyFileName1,
        setStrategyFileName1,
        campaignId,
        setCampaignId,
        campaignIdOnCreateCampaign,
        setCampaignIdOnCreateCampaign,
        socialMediaDataForPlanning,
        setSocialMediaDataForPlanning,
        campaignList,
        setCampaignList,
        assetList,
        setAssetList,
        summaryDataForApprover,
        setSummaryDataForApprover,
        hubname,
        setHubname,
        alerts,
        setAlerts,
        addAlert,
        unreadCount,
        setUnreadCount,
        selectedBusinessUnits,
        setSelectedBusinessUnits,
        selectedOfferings,
        setSelectedOfferings,
        summaryDataForCmo,
        setSummaryDataForCmo,
        summaryDataForCmoOfferings,
        setSummaryDataForCmoOfferings,
        assignedResourceList,
        setAssignedResourceList,
        fileTypeGeneratedForAdminByAiModel,
        setFileTypeGeneratedForAdminByAiModel,
        fileUrlGeneratedForAdminByAiModel,
        setFileUrlGeneratedForAdminByAiModel,
        generatedAssetIdForAdminByAiModel,
        setGeneratedAssetIdForAdminByAiModel,
        linkedInDetails,
        setLinkedInDetails,
        googleAdsDetails,
        setGoogleAdsDetails,
        metaDetails,
        setMetaDetails,
        mailchimpDetails,
        setMailchimpDetails,
        timezoneDetails,
        setTimezoneDetails,
        currentPlatformList,
        setCurrentPlatformList,
        wsUrl,
        setWsUrl,
        selected,
        setSelected,
        logout,
        authenticateConsumer,
        approvedCampaignList,
        setApprovedCampaignList,
        approvedActiveCampaignList,
        setApprovedActiveCampaignList,
        assignedResourceDropdown,
        setAssignedResourceDropdown,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
